import React, { useMemo } from 'react';
import MagicForm from '../../../../../../Components/Form/MagicForm';
import { ItemScheme } from '../../../../../../Components/Form/MagicFormItem';
import { MagicField } from '../../../../../../Enums/MagicField.enum';
import { User } from '../../../../../../graphql/graphql';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import apiClient from '../../../../../../Services/ApiClient';
import { useManagerContext } from '../../../../Services/ManagerContext';
import useRestApiClient from '../../../../../Base/Hooks/useRestApiClient';
import useScope from '../../../../../Scope/Hooks/useScope';

interface ManagerEditMemberProfileFormValues {
	salutationBefore?: string;
	salutationAfter?: string;
	firstName: string;
	lastName: string;
	phoneNumber?: string | null;
}

interface Props {
	workplaceMemberId: string;
	user: User;
	onSuccess: () => void;
}

const BASE_SCHEME: ItemScheme[] = [
	{
		type: MagicField.HSTACK,
		children: [
			{
				type: MagicField.TEXT_FIELD,
				key: 'firstName',
				title: 'base.shared.firstName',
				settings: {
					required: true,
				},
			},
			{
				type: MagicField.TEXT_FIELD,
				key: 'lastName',
				title: 'base.shared.lastName',
				settings: {
					required: true,
				},
			},
		],
	},
	{
		type: MagicField.PHONE_NUMBER,
		key: 'phoneNumber',
		title: 'component.workplaceAndWorkers.enterWorkersPhone',
	},
	{
		type: MagicField.TEXT,
		key: 'email',
		title: 'component.workplaceAndWorkers.workerEmailNotEditable',
	},
];

const DEFAULT_FORM_SCHEME: ItemScheme[] = [
	{
		type: MagicField.HSTACK,
		children: [
			{
				type: MagicField.TEXT_FIELD,
				key: 'salutationBefore',
				title: 'base.shared.salutationBefore',
			},
			{
				type: MagicField.TEXT_FIELD,
				key: 'salutationAfter',
				title: 'base.shared.salutationAfter',
			},
		],
	},
	...BASE_SCHEME,
];

const LATAM_FORM_SCHEME: ItemScheme[] = [
	{
		type: MagicField.HSTACK,
		children: [
			{
				type: MagicField.TEXT_FIELD,
				key: 'salutationBefore',
				title: 'component.managerEditMemberProfile.salutationBefore',
			},
		],
	},
	...BASE_SCHEME,
];

const ManagerEditMemberProfile: React.FC<Props> = ({
	workplaceMemberId,
	user,
	onSuccess,
}) => {
	const { displayError } = useApiClient();
	const { workplaceId } = useManagerContext();
	const { showUnexpectedError } = useRestApiClient();
	const { isLatam } = useScope();

	const handleSubmit = async (values: ManagerEditMemberProfileFormValues) => {
		try {
			//DONT LET USER DELETE PHONE NUMBER
			if (values.phoneNumber === null) {
				values.phoneNumber = undefined;
			}
			const response = await apiClient.patch(
				`/workplaces/${workplaceId}/members/${workplaceMemberId}`,
				{ user: values },
			);
			if (response.status === 200) {
				onSuccess();
			} else {
				showUnexpectedError();
			}
		} catch (e) {
			displayError(e);
		}
	};

	const defaultValues = useMemo(() => {
		const profile = user?.profile;
		return {
			salutationBefore: profile?.salutationBefore,
			salutationAfter: profile?.salutationAfter,
			firstName: profile?.firstName,
			lastName: profile?.lastName,
			phoneNumber: user?.formattedPhoneNumber,
		};
	}, [user]);

	return (
		<MagicForm
			formScheme={isLatam ? LATAM_FORM_SCHEME : DEFAULT_FORM_SCHEME}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
			submitText='base.shared.save'
		/>
	);
};

export default ManagerEditMemberProfile;
